import { seat, seatImageWithMerchantToken, seatWithMerchantToken } from '@lib/api/v2';

/**
 * 좌석 카테고리 등록
 * @param formdata seatCategoryImage, seatCategoryName
 * @returns
 */
export const postSeatCategory = (formdata: FormData) =>
  seatImageWithMerchantToken.post(`/category`, formdata);

/**
 * 좌석 카테고리 목록 조회
 * @returns
 */
export const getSeatCategory = () => seat.get(`/category`);

/**
 * 좌석 카테고리 수정
 * @param formdata seatCategoryImage, seatCategoryName
 * @returns
 */
export const patchSeatCategory = (formdata: FormData) =>
  seatImageWithMerchantToken.patch(`/category`, formdata);

/**
 * 좌석 카테고리 삭제
 * @param seatCategoryId number
 * @returns
 */
export const deleteSeatCategory = (seatCategoryId: number) =>
  seat.delete(`/category/${seatCategoryId}`);

/**
 * 이용권이 점유 가능한 좌석 목록
 * @param ticketItemId
 * @returns
 */
export const getSeatList = (ticketItemId: number) =>
  seatWithMerchantToken.get(`/list/${ticketItemId}`);

/**
 * 퇴실 예정석 조회
 * @returns
 */
export const getRemainTime = () => seatWithMerchantToken.get(`/remainTime`);

/**
 * 백오피스 좌석 이동 (v2)
 * 서버에서 트랜잭션 + SELECT FOR UPDATE + ticket 상태 가드 적용.
 * @param data ticketId, seatId (이동할 대상 좌석 id)
 */
export const patchBackofficeSeatMove = (data: { ticketId: number; seatId: number }) =>
  seatWithMerchantToken.patch(`/move`, data);

/* ─────────────────────────────────────────────
 * 좌석 배치도 배경 이미지(floorplan) — LETMEUP-343
 * 층별로 1장씩 저장(uniq merchantId+floor). 좌석 좌표(pos_*)는 변경하지 않고,
 * SeatArrayEditor(1944×2160) 최하단 레이어로 깔려 좌석과 동일 transform/scale 적용.
 * ───────────────────────────────────────────── */

export interface SeatBackground {
  id: number;
  merchantId: number;
  floor: number;
  backgroundImageUrl: string | null;
  canvasWidth: number | null;
  canvasHeight: number | null;
  opacity: number; // 0~100
  objectFit: 'cover' | 'contain' | 'fill' | 'none';
  // 배경 이미지 위치/크기 (dnd 이동·리사이즈, px / 1944×2160 캔버스 기준). null이면 전체 렌더(0,0,1944,2160)
  posX: number | null;
  posY: number | null;
  bgWidth: number | null;
  bgHeight: number | null;
}

/**
 * 좌석 배치도 배경 전체 층 조회
 * @returns SeatBackground[]
 */
export const getBackgroundImageList = () =>
  seatWithMerchantToken.get<SeatBackground[]>(`/background`);

/**
 * 층 배경 이미지 업로드 (교체 포함)
 * @param formdata backgroundImage(File), floor, canvasWidth?, canvasHeight?
 */
export const postBackgroundImage = (formdata: FormData) =>
  seatImageWithMerchantToken.post(`/background`, formdata);

/**
 * 층 배경 표시 설정 변경 (이미지 변경 없음: 투명도/맞춤/캔버스/위치/크기)
 * @param data floor, opacity?(0~100), objectFit?, canvasWidth?, canvasHeight?, posX?, posY?, bgWidth?, bgHeight?
 */
export const putBackgroundImage = (data: {
  floor: number;
  opacity?: number;
  objectFit?: SeatBackground['objectFit'];
  canvasWidth?: number;
  canvasHeight?: number;
  posX?: number;
  posY?: number;
  bgWidth?: number;
  bgHeight?: number;
}) => seatWithMerchantToken.put(`/background`, data);

/**
 * 층 배경 제거 (이미지 + 설정 삭제, S3 정리)
 * @param floor 층 번호
 */
export const deleteBackgroundImage = (floor: number) =>
  seatWithMerchantToken.delete(`/background/${floor}`);

/**
 * 좌석 카드 색상 설정 조회 (LETMEUP-1085, 인증 없음)
 * DB(tb_seat_palette) 값을 빌드 없이 반영한다. 설정이 없으면 palette=null.
 * @param merchantId number
 */
export const getSeatPaletteSetting = (merchantId: number) =>
  seat.get<{ palette: unknown }>(`/palette/${merchantId}`);
